
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export interface SearchUser {
  id: string;  
  username: string;
  first_name: string | null;
  last_name: string | null;
  profile_picture: string | null;
  friendship_status?: "none" | "pending_sent" | "pending_received" | "friends" | "rejected";
}

export const useUserSearch = () => {
  const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, isEmailConfirmed } = useAuth();
  const { t } = useTranslation('friends');

  // Debug function to test RLS policies directly
  const testRlsPolicy = async () => {
    try {
      console.log('🔍 Testing RLS policy for managed profiles...');
      const { data: currentSession } = await supabase.auth.getSession();
      console.log('Current session:', {
        userId: currentSession.session?.user?.id,
        isConfirmed: currentSession.session?.user?.email_confirmed_at,
        email: currentSession.session?.user?.email
      });

      // Test direct query to profiles
      const { data: allProfiles, error } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name')
        .ilike('username', '%ente%');
      
      console.log('Direct RLS test results:', allProfiles, error);

      // Test profile_ownerships table to verify managed profiles exist
      const { data: ownerships, error: ownershipError } = await supabase
        .from('profile_ownerships')
        .select('*');
      
      console.log('Profile ownerships:', ownerships, ownershipError);
    } catch (error) {
      console.error('RLS test failed:', error);
    }
  };

  const searchUsers = async (query: string) => {
    // Enhanced auth verification
    console.log('🔍 Search initiated with auth status:', {
      hasUser: !!user,
      userId: user?.id,
      isEmailConfirmed,
      query: query.trim()
    });

    if (!user?.id || !query.trim()) {
      setSearchResults([]);
      return;
    }

    // Wait for auth to be fully initialized
    const { data: currentSession } = await supabase.auth.getSession();
    if (!currentSession.session) {
      console.warn('⚠️ No active session found, auth may not be ready');
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const searchPattern = `%${query}%`;
      
      // Run RLS test for debugging
      if (query.includes('ente')) {
        await testRlsPolicy();
      }
      
      // Search in username, first_name, and last_name
      // RLS policies handle visibility - removed user.id filter to show all accessible profiles including managed ones
      console.log('🔍 Executing search for:', query, 'Pattern:', searchPattern);
      console.log('🔍 Current auth.uid() context:', currentSession.session.user.id);
      
      const { data: searchResults, error: searchError } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, profile_picture')
        .or(`username.ilike.${searchPattern},first_name.ilike.${searchPattern},last_name.ilike.${searchPattern}`)
        .limit(20);

      console.log('🔍 Search results received:', {
        count: searchResults?.length || 0,
        results: searchResults,
        error: searchError
      });

      if (searchError) {
        console.error('Error searching profiles:', searchError);
        toast.error(t('search.messages.requestError'));
        setSearchResults([]);
        return;
      }

      const uniqueResults = searchResults || [];

      if (uniqueResults.length === 0) {
        setSearchResults([]);
        return;
      }

      // For each user, check if there's already a friendship (bidirectional check)
      const usersWithFriendshipStatus = await Promise.all(
        uniqueResults.map(async (searchUser) => {
          try {
            // Check both directions for any existing friendship
            const { data: friendship } = await supabase
              .from('friends')
              .select('status, user_id, friend_id')
              .or(`and(user_id.eq.${user.id},friend_id.eq.${searchUser.id}),and(user_id.eq.${searchUser.id},friend_id.eq.${user.id})`)
              .maybeSingle();

            let friendship_status: "none" | "pending_sent" | "pending_received" | "friends" | "rejected" = "none";

            if (friendship) {
              if (friendship.status === "accepted") {
                friendship_status = "friends";
              } else if (friendship.status === "pending") {
                if (friendship.user_id === user.id) {
                  friendship_status = "pending_sent";
                } else {
                  friendship_status = "pending_received";
                }
              } else if (friendship.status === "rejected") {
                // Check if current user was the one who sent the rejected request
                if (friendship.user_id === user.id) {
                  friendship_status = "rejected";
                } else {
                  // If the other user sent a request that we rejected, show as "none" 
                  // so they can send us a new request
                  friendship_status = "none";
                }
              }
            }

            return {
              ...searchUser,
              friendship_status
            };
          } catch (error) {
            console.error(`Error checking friendship status for ${searchUser.username}:`, error);
            return {
              ...searchUser,
              friendship_status: "none" as const
            };
          }
        })
      );

      setSearchResults(usersWithFriendshipStatus);
    } catch (error) {
      console.error('Error in searchUsers:', error);
      toast.error(t('search.messages.requestError'));
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const sendFriendRequest = async (friendId: string) => {
    if (!user?.id) {
      toast.error(t('search.messages.loginRequired'));
      return false;
    }


    try {
      const { data, error } = await supabase.rpc('send_friend_request_with_notification', {
        requester_id: user.id,
        target_id: friendId
      });

      if (error) {
        console.error('Error sending friend request:', error);
        if (error.message.includes('already exists')) {
          toast.error(t('search.messages.alreadyExists'));
        } else {
          toast.error(t('search.messages.requestError'));
        }
        return false;
      }

      // Send email notification
      try {
        await supabase.functions.invoke('send-friendship-notification', {
          body: {
            type: 'friend_request',
            recipientUserId: friendId,
            requesterUserId: user.id
          }
        });
      } catch (emailError) {
        console.error('Error sending friend request email:', emailError);
        // Don't fail the main operation if email fails
      }

      toast.success(t('search.messages.requestSent'));
      
      // Update the search results to reflect the new status immediately
      setSearchResults(prev => 
        prev.map(user => 
          user.id === friendId 
            ? { ...user, friendship_status: "pending_sent" as const }
            : user
        )
      );
      
      return true;
    } catch (error) {
      console.error('Error in sendFriendRequest:', error);
      toast.error(t('search.messages.requestError'));
      return false;
    }
  };

  // Note: Real-time friendship updates are now handled by OptimizedRealtimeProvider
  // This prevents duplicate subscriptions and reduces query overhead
  // Search results will be automatically updated when the global realtime provider
  // invalidates the 'user-search' query cache

  return {
    searchResults,
    loading,
    searchUsers,
    sendFriendRequest
  };
};
